/**
 * Events Service - Frontend event tracking with batching and retry
 * Tracks user interactions for personalization
 */

import { supabase } from '../lib/supabase';
import API_URL from '../config/api';

// Simple logger for frontend
const logger = {
  info: (msg, data) => console.log(`[EventsService] ${msg}`, data || ''),
  error: (msg, data) => console.error(`[EventsService] ${msg}`, data || ''),
  warn: (msg, data) => console.warn(`[EventsService] ${msg}`, data || ''),
};

class EventsService {
  constructor() {
    this.baseUrl = API_URL.replace('/v1', '') || 'http://localhost:4028';
    this.eventQueue = [];
    this.flushInterval = null;
    this.flushDelay = 5000; // 5 seconds
    this.maxBatchSize = 50;
    this.isFlushing = false;
    this.retryAttempts = 3;
    this.retryDelay = 1000; // Start with 1 second
    this.optOut = false;

    // Check opt-out status
    this.checkOptOut();

    // Flush on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.flush();
      });

      // Periodic flush
      this.startFlushInterval();
    }
  }

  async checkOptOut() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data: profile, error } = await supabase
        .from('user_profiles')
        .select('personalization_opt_out')
        .eq('id', session.user.id)
        .single();

      // Silently handle all errors (404, 403, etc.)
      if (error) {
        this.optOut = false;
        return;
      }

      this.optOut = profile?.personalization_opt_out === true;
    } catch (error) {
      // Default to not opted out (silently)
      this.optOut = false;
    }
  }

  startFlushInterval() {
    if (this.flushInterval) return;

    this.flushInterval = setInterval(() => {
      if (this.eventQueue.length > 0 && !this.isFlushing) {
        this.flush();
      }
    }, this.flushDelay);
  }

  stopFlushInterval() {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }
  }

  /**
   * Track an event
   * @param {Object} event - Event data
   * @param {string} event.type - Event type (impression, click, dwell, save, like, hide, more_like)
   * @param {string} [event.itemId] - Item ID
   * @param {string} [event.query] - Search query
   * @param {string[]} [event.tags] - Tags (max 10)
   * @param {string[]} [event.colors] - Colors (max 3)
   * @param {number} [event.dwellMs] - Dwell time in milliseconds
   */
  track(event) {
    if (this.optOut) {
      // Silently ignore if opted out
      return;
    }

    // Validate event
    if (!event.type) {
      logger.warn('Event missing type', event);
      return;
    }

    const validTypes = ['impression', 'click', 'dwell', 'save', 'like', 'hide', 'more_like', 'insight_viewed', 'insight_action'];
    if (!validTypes.includes(event.type)) {
      logger.warn('Invalid event type', { type: event.type });
      return;
    }

    // Truncate arrays
    if (event.tags && event.tags.length > 10) {
      event.tags = event.tags.slice(0, 10);
    }

    if (event.colors && event.colors.length > 3) {
      event.colors = event.colors.slice(0, 3);
    }

    // Truncate query
    if (event.query && event.query.length > 500) {
      event.query = event.query.substring(0, 500);
    }

    this.eventQueue.push({
      ...event,
      timestamp: Date.now(),
    });

    // Auto-flush if queue is full
    if (this.eventQueue.length >= this.maxBatchSize) {
      this.flush();
    }
  }

  /**
   * Flush events to backend
   */
  async flush() {
    if (this.isFlushing || this.eventQueue.length === 0) {
      return;
    }

    this.isFlushing = true;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        // Silently clear queue if no session (no need to warn)
        this.eventQueue = [];
        this.isFlushing = false;
        return;
      }

      // Get batch
      const batch = this.eventQueue.splice(0, this.maxBatchSize);

      // Send to backend
      await this.sendEvents(batch);

      // Success (no logging in production)
    } catch (error) {
      // Silently drop events on error (analytics is optional)
      // In production, you might want to persist to localStorage
    } finally {
      this.isFlushing = false;
    }
  }

  /**
   * Send events to backend with retry
   */
  async sendEvents(events, attempt = 1) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No session');
      }

      const response = await fetch(`${this.baseUrl}/v1/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ events }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized');
        }
        if (response.status === 413) {
          throw new Error('Payload too large');
        }
        if (response.status === 429) {
          // Rate limited - wait longer
          const retryAfter = response.headers.get('Retry-After') || 60;
          throw new Error(`Rate limited: retry after ${retryAfter}s`);
        }

        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      // Silently handle connection refused errors (analytics service may not be running)
      if (error.message?.includes('ERR_CONNECTION_REFUSED') || 
          error.message?.includes('Failed to fetch') ||
          error.message?.includes('NetworkError')) {
        // Analytics service unavailable - silently drop events
        return;
      }
      
      if (attempt < this.retryAttempts) {
        // Exponential backoff
        const delay = this.retryDelay * Math.pow(2, attempt - 1);
        logger.warn(`Retry attempt ${attempt} after ${delay}ms`, { error: error.message });

        await new Promise(resolve => setTimeout(resolve, delay));
        return this.sendEvents(events, attempt + 1);
      }

      throw error;
    }
  }

  // Convenience methods
  trackImpression(itemId, query, tags, colors) {
    this.track({
      type: 'impression',
      itemId,
      query,
      tags,
      colors,
    });
  }

  trackClick(itemId, query, tags, colors) {
    this.track({
      type: 'click',
      itemId,
      query,
      tags,
      colors,
    });
  }

  trackDwell(itemId, dwellMs, query, tags, colors) {
    this.track({
      type: 'dwell',
      itemId,
      dwellMs,
      query,
      tags,
      colors,
    });
  }

  trackSave(itemId, query, tags, colors) {
    this.track({
      type: 'save',
      itemId,
      query,
      tags,
      colors,
    });
  }

  trackLike(itemId, query, tags, colors) {
    this.track({
      type: 'like',
      itemId,
      query,
      tags,
      colors,
    });
  }

  trackHide(itemId, query, tags, colors) {
    this.track({
      type: 'hide',
      itemId,
      query,
      tags,
      colors,
    });
  }

  trackMoreLike(itemId, query, tags, colors) {
    this.track({
      type: 'more_like',
      itemId,
      query,
      tags,
      colors,
    });
  }

  // Stage 7 Part 2: Insight tracking
  trackInsightViewed(insightId, type, positionInFeed) {
    this.track({
      type: 'insight_viewed',
      insightId,
      insightType: type,
      positionInFeed,
    });
  }

  trackInsightAction(insightId, actionType, actionValue) {
    this.track({
      type: 'insight_action',
      insightId,
      actionType, // 'see_evidence' | 'try_suggestion' | 'explore_entity'
      actionValue,
    });
  }
}

// Export singleton
export const eventsService = new EventsService();

